import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { Equipamento } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';
import { EfetivoService, PostoService } from '@core/services/domain-services';
import { ManutencaoEquipamentoService } from '@shared/api/services/manutencao-equipamento.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { environment } from '@environments/environment';

export type ResultadoInspecao = 'Aprovado' | 'Com Observações' | 'Reprovado';

export interface InspecaoArma {
  id: string | number;
  equipamentoId?: number;
  equipamentoNome?: string;
  equipamentoCodigo?: string;
  tipo: string;
  resultado: ResultadoInspecao;
  data: string;
  hora?: string;
  responsavel: string;
  postoId?: number | null;
  postoNome?: string;
  observacoes?: string;
  encaminhamento?: string;
  frequenciaDefinida?: string;
  proximaInspecao?: string;
  fotoGeralNome?: string;
  fotoGeralPreview?: string;
  fotoDetalheNome?: string;
  fotoDetalhePreview?: string;
  relatorioPdfNome?: string;
  criadoEm?: string;
}

export interface HistoricoArmaEvento {
  id: string | number;
  equipamentoId?: number;
  origem: string;
  titulo: string;
  descricao: string;
  resultado: ResultadoInspecao;
  encaminhamento: string;
  responsavel: string;
  data: string;
  criadoEm: string;
}

@Component({
  selector: 'app-equipamento-inspecao-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    DrawerModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    TooltipModule,
  ],
  templateUrl: './equipamento-inspecao-tab.component.html',
  styleUrl: './equipamento-inspecao-tab.component.scss',
})
export class EquipamentoInspecaoTabComponent implements OnInit, OnChanges {
  private readonly feedback = inject(FeedbackService);
  private readonly manutencaoSvc = inject(ManutencaoEquipamentoService);
  private readonly postoSvc = inject(PostoService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  @Input() equipamento: Equipamento | null = null;
  @Output() countChange = new EventEmitter<number>();
  @Output() inspecaoChange = new EventEmitter<InspecaoArma>();

  inspecoes: InspecaoArma[] = [];
  historicoEncaminhamentos: HistoricoArmaEvento[] = [];
  drawerVisible = false;
  saving = false;

  postos: Array<{ label: string; value: number | string }> = [];
  efetivos: Array<{ label: string; value: string }> = [];

  readonly opcoesPeriodicidade = [
    { label: 'Mensal', value: 'Mensal' },
    { label: 'Trimestral', value: 'Trimestral' },
    { label: 'Semestral', value: 'Semestral' },
    { label: 'Anual', value: 'Anual' },
  ];

  readonly opcoesEncaminhamento = [
    { label: 'Enviar para manutenção corretiva', value: 'Enviada para manutenção corretiva' },
    { label: 'Substituição de componente / peça', value: 'Substituição de peça' },
    { label: 'Recolha ao armeiro para avaliação', value: 'Recolha ao armeiro' },
    { label: 'Acompanhamento técnico reforçado', value: 'Acompanhamento técnico' },
  ];

  form = {
    inspecionadoPor: '',
    data: new Date() as Date | null,
    hora: '09:00',
    postoId: null as number | null,
    postoNome: '',
    resultado: 'Aprovado' as ResultadoInspecao,
    observacoes: '',
    periodicidade: 'Trimestral',
    proximaData: new Date() as Date | null,
    encaminhamento: 'Enviada para manutenção corretiva',
    gerarHistorico: true,
  };

  // Uploads
  fotoGeralFile: File | null = null;
  fotoGeralPreview: string | null = null;
  fotoDetalheFile: File | null = null;
  fotoDetalhePreview: string | null = null;
  relatorioPdfFile: File | null = null;
  relatorioPdfNome: string | null = null;

  ngOnInit(): void {
    this.carregarListasAuxiliares();
    this.inicializarDados();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equipamento'] && this.equipamento) {
      this.inicializarDados();
    }
  }

  private inicializarDados(): void {
    if (!this.equipamento?.id) return;
    this.carregarInspecoes();
  }

  get armaDisplayName(): string {
    if (!this.equipamento) return 'Arma';
    const marca = this.getPropriedadeValor('MARCA') ?? this.equipamento.marca;
    const modelo = this.getPropriedadeValor('MODELO') ?? this.equipamento.modelo;
    const tipo = this.getPropriedadeValor('TIPO');
    const calibre = this.getPropriedadeValor('CALIBRE');

    const marcaModelo = [marca, modelo].filter(Boolean).join(' ');
    const parts = [tipo, marcaModelo, calibre].filter(Boolean);
    if (parts.length) return parts.join(' · ');

    return (
      this.equipamento.tipoEquipamento?.designacao?.trim() ||
      this.equipamento.codigoInterno ||
      'Arma'
    );
  }

  get armaReferencia(): string {
    if (!this.equipamento) return '';
    const numSerie =
      this.getPropriedadeValor('NUMERO_SERIE') ??
      this.getPropriedadeValor('NUMERO DE SERIE') ??
      this.equipamento.numeroDeSerie;
    const codInterno = this.equipamento.codigoInterno;

    const parts = [];
    if (numSerie) parts.push(`Nº Série: ${numSerie}`);
    if (codInterno) parts.push(`Ref: ${codInterno}`);
    return parts.join(' · ') || 'Sem número de série';
  }

  getPropriedadeValor(nome: string): string | null {
    if (!this.equipamento?.propriedades) return null;
    return (
      this.equipamento.propriedades.find(
        (p) => p.nome?.toUpperCase() === nome.toUpperCase(),
      )?.valor ?? null
    );
  }

  get frequenciaCadastro(): string {
    if (!this.equipamento?.propriedades) return 'Trimestral';
    const prop = this.equipamento.propriedades.find(
      (p) =>
        p.nome?.toUpperCase() === 'FREQUENCIA_VERIFICACAO' ||
        p.nome?.toUpperCase() === 'FREQUENCIA' ||
        p.nome?.toUpperCase() === 'PERIODICIDADE' ||
        p.nome?.toUpperCase() === 'FREQUENCIA_INSPECAO',
    );
    if (!prop?.valor) return 'Trimestral';
    const map: Record<string, string> = {
      diaria: 'Diária',
      semanal: 'Semanal',
      quinzenal: 'Quinzenal',
      mensal: 'Mensal',
      trimestral: 'Trimestral',
      semestral: 'Semestral',
      anual: 'Anual',
    };
    const key = prop.valor.toLowerCase().trim();
    return map[key] ?? prop.valor;
  }

  get proximaDataObrigatoriaCadastro(): string | null {
    if (!this.equipamento?.propriedades) return null;
    const prop = this.equipamento.propriedades.find(
      (p) =>
        p.nome?.toUpperCase() === 'PROXIMA_VERIFICACAO_OBRIGATORIA' ||
        p.nome?.toUpperCase() === 'PROXIMA_VERIFICACAO' ||
        p.nome?.toUpperCase() === 'PROXIMA_INSPECAO',
    );
    return prop?.valor || null;
  }

  get ultimaInspecao(): InspecaoArma | null {
    if (!this.inspecoes.length) return null;
    return this.inspecoes[0];
  }

  get proximaInspecaoData(): string {
    if (this.ultimaInspecao?.data) {
      const dataUltima = new Date(this.ultimaInspecao.data);
      if (!isNaN(dataUltima.getTime())) {
        const freq = this.ultimaInspecao.frequenciaDefinida || this.frequenciaCadastro;
        const calc = this.calcularProximaData(dataUltima, freq);
        return this.formatDateISO(calc);
      }
    }

    if (this.proximaDataObrigatoriaCadastro) {
      return this.proximaDataObrigatoriaCadastro;
    }

    const hoje = new Date();
    const calc = this.calcularProximaData(hoje, this.frequenciaCadastro);
    return this.formatDateISO(calc);
  }

  get isProximaAtrasada(): boolean {
    if (!this.proximaInspecaoData) return false;
    const dataPrev = new Date(this.proximaInspecaoData);
    if (isNaN(dataPrev.getTime())) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return dataPrev < hoje;
  }

  private carregarListasAuxiliares(): void {
    // Carregar postos
    this.postoSvc
      .getAll({ pageSize: 150 })
      .pipe(
        catchError(() => of({ data: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: any) => {
        const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.postos = items.map((p: any) => ({
          label: p.nome || `Posto #${p.id}`,
          value: Number(p.id),
        }));

        if (!this.postos.length) {
          this.postos = [
            { label: 'Posto Central', value: 1 },
            { label: 'Posto Armeiro Luanda', value: 2 },
            { label: 'Posto VIP / Escolta', value: 3 },
            { label: 'Posto Operacional Kilamba', value: 4 },
          ];
        }
      });

    // Carregar efetivos / guardas
    this.efetivoSvc
      .getAll({ pageSize: 150 })
      .pipe(
        catchError(() => of({ data: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: any) => {
        const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.efetivos = items.map((e: any) => ({
          label: `${e.nome || 'Efetivo'} (${e.codigo || e.cargo || 'Guarda'})`,
          value: e.nome || 'Responsável',
        }));

        if (!this.efetivos.length) {
          this.efetivos = [
            { label: 'João Manuel (Supervisor de Armamento)', value: 'João Manuel' },
            { label: 'Carlos Mendes (Técnico de Manutenção / Armeiro)', value: 'Carlos Mendes' },
            { label: 'Admin Silva (Gestor de Frota e Equipamentos)', value: 'Admin Silva' },
            { label: 'António Kipungo (Inspetor de Segurança)', value: 'António Kipungo' },
          ];
        }
      });
  }

  carregarInspecoes(): void {
    if (!this.equipamento?.id) return;
    const equipId = Number(this.equipamento.id);
    const storageKey = `sp_inspecoes_arma_${equipId}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        this.inspecoes = JSON.parse(stored);
      } catch {
        this.inspecoes = [];
      }
    } else {
      // Dados padrão/seed representativos da arma (exatamente como no mockup)
      this.inspecoes = [
        {
          id: 1,
          equipamentoId: equipId,
          equipamentoNome: this.armaDisplayName,
          tipo: 'Inspeção periódica',
          resultado: 'Aprovado',
          data: '2026-04-10',
          hora: '10:00',
          responsavel: 'João Manuel',
          postoNome: this.equipamento.posto?.nome || 'Posto Central',
          observacoes: 'Funcionamento e estado geral conformes',
          frequenciaDefinida: 'Trimestral',
          proximaInspecao: '2026-07-10',
          criadoEm: '2026-04-10T10:00:00Z',
        },
        {
          id: 2,
          equipamentoId: equipId,
          equipamentoNome: this.armaDisplayName,
          tipo: 'Inspeção periódica',
          resultado: 'Com Observações',
          data: '2026-01-10',
          hora: '09:30',
          responsavel: 'Carlos Mendes',
          postoNome: this.equipamento.posto?.nome || 'Posto Central',
          observacoes: 'Ligeiro desgaste na coronha',
          frequenciaDefinida: 'Trimestral',
          proximaInspecao: '2026-04-10',
          criadoEm: '2026-01-10T09:30:00Z',
        },
        {
          id: 3,
          equipamentoId: equipId,
          equipamentoNome: this.armaDisplayName,
          tipo: 'Inspeção periódica',
          resultado: 'Reprovado',
          data: '2025-10-10',
          hora: '14:15',
          responsavel: 'Admin Silva',
          postoNome: this.equipamento.posto?.nome || 'Posto Central',
          observacoes: 'Falha no mecanismo de segurança',
          encaminhamento: 'Enviada para manutenção corretiva',
          frequenciaDefinida: 'Trimestral',
          proximaInspecao: '2026-01-10',
          criadoEm: '2025-10-10T14:15:00Z',
        },
      ];
      localStorage.setItem(storageKey, JSON.stringify(this.inspecoes));
    }

    this.carregarHistoricoEncaminhamentos();
    this.countChange.emit(this.inspecoes.length);
  }

  carregarHistoricoEncaminhamentos(): void {
    if (!this.equipamento?.id) return;
    const equipId = Number(this.equipamento.id);
    const histKey = `sp_historico_arma_${equipId}`;
    const stored = localStorage.getItem(histKey);
    if (stored) {
      try {
        this.historicoEncaminhamentos = JSON.parse(stored);
      } catch {
        this.historicoEncaminhamentos = [];
      }
    } else {
      this.historicoEncaminhamentos = [];
    }
  }

  abrirDrawerRegisto(): void {
    const hoje = new Date();
    const horaAtual = `${String(hoje.getHours()).padStart(2, '0')}:${String(hoje.getMinutes()).padStart(2, '0')}`;
    const defaultResp = this.efetivos[0]?.value || 'João Manuel';
    const currentPostoId = this.equipamento?.idPosto ?? this.equipamento?.posto?.id ?? null;
    const currentPostoNome = this.equipamento?.posto?.nome ?? '';
    const periodicidadePadrao = this.frequenciaCadastro || 'Trimestral';
    const proximaCalc = this.calcularProximaData(hoje, periodicidadePadrao);

    this.form = {
      inspecionadoPor: defaultResp,
      data: hoje,
      hora: horaAtual,
      postoId: currentPostoId,
      postoNome: currentPostoNome,
      resultado: 'Aprovado',
      observacoes: 'Funcionamento e estado geral conformes.',
      periodicidade: periodicidadePadrao,
      proximaData: proximaCalc,
      encaminhamento: 'Enviada para manutenção corretiva',
      gerarHistorico: true,
    };

    this.fotoGeralFile = null;
    this.fotoGeralPreview = null;
    this.fotoDetalheFile = null;
    this.fotoDetalhePreview = null;
    this.relatorioPdfFile = null;
    this.relatorioPdfNome = null;

    this.drawerVisible = true;
  }

  fecharDrawer(): void {
    if (this.saving) return;
    this.drawerVisible = false;
  }

  onPostoChange(event: any): void {
    const found = this.postos.find((p) => p.value === event.value);
    if (found) {
      this.form.postoNome = found.label;
    }
  }

  onDataOrPeriodicidadeChange(): void {
    const base = this.form.data instanceof Date ? this.form.data : new Date();
    this.form.proximaData = this.calcularProximaData(base, this.form.periodicidade);
  }

  onResultadoChange(res: ResultadoInspecao): void {
    this.form.resultado = res;
    if (res === 'Aprovado' && !this.form.observacoes.trim()) {
      this.form.observacoes = 'Funcionamento e estado geral conformes.';
    } else if (res === 'Com Observações' && this.form.observacoes === 'Funcionamento e estado geral conformes.') {
      this.form.observacoes = 'Ligeiro desgaste verificado durante a inspeção.';
    } else if (res === 'Reprovado' && this.form.observacoes === 'Funcionamento e estado geral conformes.') {
      this.form.observacoes = 'Falha detectada no mecanismo de disparo / segurança. Retirar de serviço para reparação.';
    }
  }

  // Upload handlers
  async onFotoGeralSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      this.fotoGeralFile = await compressImageIfNeeded(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        this.fotoGeralPreview = e.target?.result as string;
      };
      reader.readAsDataURL(this.fotoGeralFile);
    }
  }

  removerFotoGeral(): void {
    this.fotoGeralFile = null;
    this.fotoGeralPreview = null;
  }

  async onFotoDetalheSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      this.fotoDetalheFile = await compressImageIfNeeded(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        this.fotoDetalhePreview = e.target?.result as string;
      };
      reader.readAsDataURL(this.fotoDetalheFile);
    }
  }

  removerFotoDetalhe(): void {
    this.fotoDetalheFile = null;
    this.fotoDetalhePreview = null;
  }

  onRelatorioSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      this.relatorioPdfFile = file;
      this.relatorioPdfNome = file.name;
    }
  }

  removerRelatorio(): void {
    this.relatorioPdfFile = null;
    this.relatorioPdfNome = null;
  }

  salvarInspecao(): void {
    if (!this.equipamento?.id) {
      this.feedback.showWarning('Equipamento não identificado.');
      return;
    }

    if (!this.form.data) {
      this.feedback.showWarning('Informe a data da inspeção.');
      return;
    }

    if (!this.form.inspecionadoPor?.trim()) {
      this.feedback.showWarning('Selecione ou informe quem inspecionou a arma.');
      return;
    }

    this.saving = true;
    const equipId = Number(this.equipamento.id);
    const dataInspecaoISO = this.formatDateISO(this.form.data);
    const proximaCalc = this.form.proximaData
      ? this.formatDateISO(this.form.proximaData)
      : this.formatDateISO(this.calcularProximaData(this.form.data, this.form.periodicidade));

    const novaInspecao: InspecaoArma = {
      id: Date.now(),
      equipamentoId: equipId,
      equipamentoNome: this.armaDisplayName,
      equipamentoCodigo: this.equipamento.codigoInterno,
      tipo: 'Inspeção periódica',
      resultado: this.form.resultado,
      data: dataInspecaoISO,
      hora: this.form.hora || '09:00',
      responsavel: this.form.inspecionadoPor.trim(),
      postoId: this.form.postoId,
      postoNome: this.form.postoNome || this.equipamento.posto?.nome || 'Posto Central',
      observacoes: this.form.observacoes.trim(),
      encaminhamento:
        this.form.resultado !== 'Aprovado' ? this.form.encaminhamento : undefined,
      frequenciaDefinida: this.form.periodicidade,
      proximaInspecao: proximaCalc,
      fotoGeralNome: this.fotoGeralFile?.name,
      fotoGeralPreview: this.fotoGeralPreview ?? undefined,
      fotoDetalheNome: this.fotoDetalheFile?.name,
      fotoDetalhePreview: this.fotoDetalhePreview ?? undefined,
      relatorioPdfNome: this.relatorioPdfNome ?? undefined,
      criadoEm: new Date().toISOString(),
    };

    // 1. Guardar na lista de inspeções
    this.inspecoes.unshift(novaInspecao);
    localStorage.setItem(`sp_inspecoes_arma_${equipId}`, JSON.stringify(this.inspecoes));

    // 2. Encaminhamento automático: se 'Reprovado' ou 'Com Observações', gera entrada no Histórico da arma
    if (this.form.resultado !== 'Aprovado' && this.form.gerarHistorico) {
      const novoEventoHistorico: HistoricoArmaEvento = {
        id: Date.now(),
        equipamentoId: equipId,
        origem: 'Inspeção de Arma',
        titulo: `Inspeção: ${this.form.resultado}`,
        descricao: `${this.form.observacoes || 'Sem observações'} · Encaminhamento: ${this.form.encaminhamento}`,
        resultado: this.form.resultado,
        encaminhamento: this.form.encaminhamento,
        responsavel: this.form.inspecionadoPor.trim(),
        data: dataInspecaoISO,
        criadoEm: new Date().toISOString(),
      };

      this.historicoEncaminhamentos.unshift(novoEventoHistorico);
      localStorage.setItem(
        `sp_historico_arma_${equipId}`,
        JSON.stringify(this.historicoEncaminhamentos),
      );

      // Sincronizar ordem de reparação/manutenção se reprovada
      if (this.form.resultado === 'Reprovado') {
        const payloadManutencao = {
          equipamentoId: equipId,
          dataManutencao: this.form.data,
          manutencaoTipoEnum: 1, // Reparação
          manutencaoEstadoEnum: 0, // Planeada
          custo: 0,
          descricaoServico: `[Inspeção Reprovada] ${this.form.encaminhamento} - ${this.form.observacoes}`,
          observacoes: `Responsável pela verificação: ${this.form.inspecionadoPor}`,
          quilometragemOuHorasUso: 0,
        };

        this.manutencaoSvc
          .criar(payloadManutencao as any)
          .pipe(
            catchError((err) => {
              console.warn('Sincronização com backend ManutencaoEquipamento (modo offline):', err);
              return of(null);
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    }

    // 3. Upload de anexos para a Tab de Documentação do Equipamento
    if (this.relatorioPdfFile) {
      const fd = new FormData();
      fd.append('Designacao', `Relatório de Inspeção - ${this.formatDate(this.form.data)} (${this.form.resultado})`);
      fd.append('Ficheiro', this.relatorioPdfFile, this.relatorioPdfFile.name);
      this.http
        .put<any>(`${environment.apiurl}/api/Equipamentos/${equipId}/AdicionarDocumento`, fd)
        .pipe(
          catchError((e) => of(null)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    if (this.fotoGeralFile) {
      const fd = new FormData();
      fd.append('Designacao', `Foto Geral da Inspeção - ${this.formatDate(this.form.data)}`);
      fd.append('Ficheiro', this.fotoGeralFile, this.fotoGeralFile.name);
      this.http
        .put<any>(`${environment.apiurl}/api/Equipamentos/${equipId}/AdicionarDocumento`, fd)
        .pipe(
          catchError((e) => of(null)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.saving = false;
    this.drawerVisible = false;
    this.countChange.emit(this.inspecoes.length);
    this.inspecaoChange.emit(novaInspecao);

    if (this.form.resultado === 'Aprovado') {
      this.feedback.showSuccess('Inspeção registada com sucesso! A arma está operacional sem restrições.');
    } else {
      this.feedback.showSuccess(
        `Inspeção registada (${this.form.resultado}). Encaminhamento automático gerado no histórico da arma.`,
      );
    }
  }

  calcularProximaData(dataBase: Date, frequencia: string): Date {
    const proxima = new Date(dataBase);
    const freq = (frequencia || '').toLowerCase().trim();

    if (freq.includes('mensal') && !freq.includes('trimestral') && !freq.includes('semestral')) {
      proxima.setMonth(proxima.getMonth() + 1);
    } else if (freq.includes('trimestral')) {
      proxima.setMonth(proxima.getMonth() + 3);
    } else if (freq.includes('semestral')) {
      proxima.setMonth(proxima.getMonth() + 6);
    } else if (freq.includes('anual')) {
      proxima.setFullYear(proxima.getFullYear() + 1);
    } else if (freq.includes('diar')) {
      proxima.setDate(proxima.getDate() + 1);
    } else if (freq.includes('semanal')) {
      proxima.setDate(proxima.getDate() + 7);
    } else {
      proxima.setMonth(proxima.getMonth() + 3);
    }

    return proxima;
  }

  getResultadoClass(res?: ResultadoInspecao): string {
    switch (res) {
      case 'Aprovado':
        return 'res-aprovado';
      case 'Com Observações':
        return 'res-observacoes';
      case 'Reprovado':
        return 'res-reprovado';
      default:
        return 'res-default';
    }
  }

  getResultadoIcon(res?: ResultadoInspecao): string {
    switch (res) {
      case 'Aprovado':
        return 'ri-checkbox-circle-fill';
      case 'Com Observações':
        return 'ri-error-warning-fill';
      case 'Reprovado':
        return 'ri-close-circle-fill';
      default:
        return 'ri-information-fill';
    }
  }

  formatDate(d?: string | Date | null): string {
    if (!d) return '—';
    try {
      const date = typeof d === 'string' ? new Date(d) : d;
      return date.toLocaleDateString('pt-AO');
    } catch {
      return String(d);
    }
  }

  private formatDateISO(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
